import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import PageTitle from "../components/common/PageTitle";
import ArchitectChatPanel from "../components/architect/ArchitectChatPanel";
import ApiSpecEditorPanel from "../components/architect/ApiSpecEditorPanel";
import ArchitectReviewPanel from "../components/architect/ArchitectReviewPanel";
import { useArchitectAgentChat } from "../hooks/useArchitectAgentChat";
import { apiService } from "../api/services/apiService";
import type { API } from "../types";
import { EntityStatus } from "../types";
import { formatApiDisplayName } from "../utils/apiDisplayName";
import { getTenantId } from "../utils/jwtUtils";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";
import {
  extractArchitectReviewFromMessage,
  extractOpenApiJsonFromMessage,
  parseArchitectReviewJson,
  serializeArchitectReview,
  type ArchitectReviewPayload,
} from "../utils/architectReview";
import { extractArchitectureStandardFromMessage } from "../utils/architectureStandardFence";
import { PM_HUBS } from "../utils/pmHubRoutes";
import {
  ARCHITECTURE_ARTIFACT_LABELS,
  ARCHITECTURE_PHASE_LABELS,
  type ArchitectureArtifactType,
  type ArchitectureLifecyclePhase,
} from "../utils/architectureStandards";

const ARCHITECT_TAB_SLUGS = ["build", "enhance", "platform-standards"] as const;

const BUILD_PROMPTS = [
  "Help me design a REST API for user profile management",
  "Review the draft OpenAPI spec against platform standards",
];

const PLATFORM_STANDARDS_PROMPTS = [
  "Seed starter architecture standards",
  "Draft Kubernetes namespace design standard",
  "Update Apigee proxy testing standard",
  "List existing microservice decommissioning standards",
];

const ENHANCE_PROMPTS = (apiName: string) => [
  `Enhance documentation for API "${apiName}"`,
  `Infer and suggest tags for API "${apiName}"`,
  `Run a standards review for API "${apiName}" and output architect-review JSON`,
  `Apply documentation improvements, tags, and standards review for API uuid: `,
];

const ArchitectAgentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tabIndex: tab, setTabIndex: setTab } = useBookmarkableTab({
    slugs: ARCHITECT_TAB_SLUGS,
  });
  const { messages, sendMessage, loading, error, contextUsage } = useArchitectAgentChat();

  const [specContent, setSpecContent] = useState("");
  const [review, setReview] = useState<ArchitectReviewPayload | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);
  const [tags, setTags] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [apis, setApis] = useState<API[]>([]);
  const [selectedApi, setSelectedApi] = useState<API | null>(null);
  const [proposedSpec, setProposedSpec] = useState("");
  const [chatMaximized, setChatMaximized] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [standardFence, setStandardFence] = useState<ReturnType<
    typeof extractArchitectureStandardFromMessage
  >>(null);

  useEffect(() => {
    apiService.getApis().then(setApis).catch(() => setApis([]));
  }, []);

  useEffect(() => {
    const apiUuid = searchParams.get("api");
    if (!apiUuid || apis.length === 0) return;
    const match = apis.find((api) => api.uuid === apiUuid);
    if (match) {
      setSelectedApi(match);
    }
  }, [apis, searchParams]);

  const lastAssistant = useMemo(() => {
    const assistants = messages.filter((m) => m.role === "assistant");
    return assistants[assistants.length - 1]?.content ?? "";
  }, [messages]);

  useEffect(() => {
    const spec = extractOpenApiJsonFromMessage(lastAssistant);
    if (spec) {
      if (tab === 0) setSpecContent(spec);
      else setProposedSpec(spec);
    }
    const extracted = extractArchitectReviewFromMessage(lastAssistant);
    if (extracted) setReview(extracted);
    const standard = extractArchitectureStandardFromMessage(lastAssistant);
    if (standard) setStandardFence(standard);
  }, [lastAssistant, tab]);

  useEffect(() => {
    if (tab === 1 && selectedApi) {
      setSpecContent(selectedApi.specContent ?? "");
      setProposedSpec(selectedApi.specContent ?? "");
      setName(selectedApi.name);
      setDescription(selectedApi.description ?? "");
      setVersion(selectedApi.version);
      setStatus(selectedApi.status);
      setTags((selectedApi.tags ?? []).join(", "));
      setReview(parseArchitectReviewJson(selectedApi.architectReview));
    }
  }, [selectedApi, tab]);

  useEffect(() => {
    setPreviewOpen(false);
  }, [selectedApi?.uuid]);

  const swaggerSpec = useMemo(() => {
    const raw = tab === 1 ? proposedSpec || specContent : specContent;
    if (!raw.trim()) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [specContent, proposedSpec, tab]);

  const handleSaveNew = async () => {
    const tenantId = getTenantId();
    if (!tenantId || !name.trim() || !specContent.trim()) {
      setSaveError("Name, tenant, and valid spec are required.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      JSON.parse(specContent);
      await apiService.createApi({
        tenantId,
        name: name.trim(),
        description: description.trim() || undefined,
        version: version.trim() || "1.0.0",
        status,
        specContent,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        architectReview: review ? serializeArchitectReview(review) : undefined,
      });
      navigate("/api-explorer");
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Failed to save API");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEnhance = async () => {
    if (!selectedApi) return;
    const content = proposedSpec.trim() || specContent;
    if (!content.trim()) {
      setSaveError("No spec content to save.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      JSON.parse(content);
      await apiService.updateApi(selectedApi.uuid, {
        name: name.trim() || selectedApi.name,
        description: description.trim() || undefined,
        version: version.trim() || selectedApi.version,
        status,
        specContent: content,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        architectReview: review ? serializeArchitectReview(review) : undefined,
      });
      navigate(`/api-explorer/${selectedApi.uuid}`);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Failed to update API");
    } finally {
      setSaving(false);
    }
  };

  const enhanceQuickPrompts = selectedApi
    ? [
        ...ENHANCE_PROMPTS(selectedApi.name).slice(0, 3),
        `${ENHANCE_PROMPTS(selectedApi.name)[3]}${selectedApi.uuid}`,
      ]
    : [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Architect Agent"
        pageDescription="Design OpenAPI specs, enhance catalog APIs, and author platform architecture standards with AI."
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Button
          component={RouterLink}
          to="/agent-dashboard"
          sx={{ mb: 2, textTransform: "none" }}
        >
          Back to Agents
        </Button>

        <Tabs value={tab} onChange={setTab} sx={{ mb: 3 }}>
          <Tab label="Build new API" />
          <Tab label="Enhance existing API" />
          <Tab label="Platform standards" />
        </Tabs>

        <Grid container spacing={2}>
          <Grid item xs={12} md={chatMaximized ? 12 : 5}>
            {tab === 1 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={apis}
                    getOptionLabel={(a) =>
                      `${formatApiDisplayName(a.name)} (${a.version})`
                    }
                    value={selectedApi}
                    onChange={(_, v) => {
                      setSelectedApi(v);
                      setSearchParams(
                        (prev) => {
                          const next = new URLSearchParams(prev);
                          if (v) {
                            next.set("api", v.uuid);
                          } else {
                            next.delete("api");
                          }
                          return next;
                        },
                        { replace: true },
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select API" size="small" />
                    )}
                  />
                  {selectedApi && (
                    <Tooltip
                      title={
                        swaggerSpec
                          ? "Open API preview (maximized)"
                          : "Valid OpenAPI JSON required to preview"
                      }
                    >
                      <span>
                        <IconButton
                          color="primary"
                          disabled={!swaggerSpec}
                          onClick={() => setPreviewOpen(true)}
                          aria-label="Open API preview"
                          sx={{ mt: 0.25 }}
                        >
                          <PreviewIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            )}
            <ArchitectChatPanel
              messages={messages}
              onSend={sendMessage}
              loading={loading}
              error={error}
              contextUsage={contextUsage}
              quickPrompts={
                tab === 0
                  ? BUILD_PROMPTS
                  : tab === 2
                    ? PLATFORM_STANDARDS_PROMPTS
                    : enhanceQuickPrompts
              }
              maximized={chatMaximized}
              onToggleMaximize={() => setChatMaximized((v) => !v)}
            />
          </Grid>
          {!chatMaximized && tab !== 2 && (
          <Grid item xs={12} md={7}>
            <ApiSpecEditorPanel
              specContent={tab === 1 ? proposedSpec || specContent : specContent}
              onChange={(v) => (tab === 1 ? setProposedSpec(v) : setSpecContent(v))}
            />
          </Grid>
          )}
          {!chatMaximized && tab === 2 && (
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Architecture standards catalog
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seed or edit lifecycle standards (design through decommissioning) for
                microservices, Apigee, API management, and Kubernetes. CMS documents
                are the source of truth after seed.
              </Typography>
              <Button
                component={RouterLink}
                to={PM_HUBS.architectureStandards}
                variant="outlined"
                sx={{ textTransform: "none", mb: 2 }}
              >
                Open Architecture Standards Hub
              </Button>
              {standardFence && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {standardFence.action ?? "update"} —{" "}
                  {standardFence.name ??
                    `${ARCHITECTURE_ARTIFACT_LABELS[standardFence.artifactType as ArchitectureArtifactType] ?? standardFence.artifactType ?? ""} ${ARCHITECTURE_PHASE_LABELS[standardFence.lifecyclePhase as ArchitectureLifecyclePhase] ?? standardFence.lifecyclePhase ?? ""}`}
                  {standardFence.status ? ` (${standardFence.status})` : ""}
                </Alert>
              )}
            </Paper>
          </Grid>
          )}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2, display: chatMaximized || tab === 2 ? "none" : undefined }}>
          <Grid item xs={12} md={6}>
            <ArchitectReviewPanel review={review} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Save to CMS
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="API name"
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Description"
                  size="small"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Version"
                  size="small"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                />
                <TextField
                  label="Tags (comma-separated)"
                  size="small"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  fullWidth
                />
                <TextField
                  select
                  label="Status"
                  size="small"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EntityStatus)}
                >
                  {Object.values(EntityStatus).map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                {saveError && <Alert severity="error">{saveError}</Alert>}
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={tab === 0 ? handleSaveNew : handleSaveEnhance}
                >
                  {tab === 0 ? "Create API in CMS" : "Update API in CMS"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          fullScreen
          open={previewOpen && !!swaggerSpec}
          onClose={() => setPreviewOpen(false)}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Typography variant="h6" component="span">
              API preview
              {selectedApi ? ` — ${selectedApi.name}` : ""}
            </Typography>
            <IconButton
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2, bgcolor: "background.default" }}>
            {swaggerSpec && <SwaggerUI spec={swaggerSpec} docExpansion="list" />}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ArchitectAgentPage;
